import { KeyboardEvent, ReactNode, useId, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoOutline from '@mui/icons-material/InfoOutline';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

interface AlertCardProps<T = unknown> {
  severity: 'error' | 'warning' | 'info' | 'success';
  message: ReactNode;
  items?: T[];
  renderItem?: (item: T, index: number) => ReactNode;

  defaultExpanded?: boolean;

  itemsThresholdBeforeScroll?: number;
  listMaxHeight?: number;

  sx?: SxProps<Theme>;
}

export default function AlertCard<T = unknown>({
  severity,
  message,
  items,
  renderItem,
  defaultExpanded = true,
  itemsThresholdBeforeScroll,
  listMaxHeight = 180,
  sx,
}: AlertCardProps<T>) {
  const theme = useTheme();

  const [expanded, setExpanded] = useState(defaultExpanded);

  const collapseId = useId();

  const isAccordion = Boolean(items && items.length > 0 && renderItem);

  const handleToggle = () => {
    if (isAccordion) {
      setExpanded((prev) => !prev);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isAccordion && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault(); // Prevent page scrolling on Space
      handleToggle();
    }
  };

  if (items && items.length === 0) {
    return null;
  }

  const severityMap = {
    error: {
      icon: ErrorOutline,
      light: theme.palette.semantic.card.error,
      border: theme.palette.semantic.text.error,
      text: theme.palette.semantic.text.error,
    },
    warning: {
      icon: WarningAmberOutlined,
      light: theme.palette.semantic.card.warning,
      border: theme.palette.semantic.outline.warning,
      text: theme.palette.semantic.text.warning,
    },
    info: {
      icon: InfoOutline,
      light: theme.palette.semantic.card.info,
      border: theme.palette.semantic.text.info,
      text: theme.palette.semantic.text.info,
    },
    success: {
      icon: CheckCircleOutline,
      light: theme.palette.semantic.card.success,
      border: theme.palette.semantic.text.success,
      text: theme.palette.semantic.text.success,
    },
  };

  const colors = severityMap[severity];
  const Icon = colors.icon;
  const scrollbar = theme.palette.semantic.scrollbar[severity];

  const listSx =
    items &&
    itemsThresholdBeforeScroll &&
    items.length >= itemsThresholdBeforeScroll
      ? {
          maxHeight: listMaxHeight,
          overflowY: 'auto',
          scrollbarColor: `${scrollbar.toggle} ${scrollbar.bg}`,
          opacity: 0.6,
          '&::-webkit-scrollbar-track': {
            backgroundColor: scrollbar.bg,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: scrollbar.toggle,
            '&:hover': {
              backgroundColor: scrollbar.hover,
            },
          },
        }
      : {};

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: colors.border,
        bgcolor: colors.light,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        {...(isAccordion && {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': expanded,
          'aria-controls': collapseId,
          onClick: handleToggle,
          onKeyDown: handleKeyDown,
        })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: isAccordion ? 'pointer' : 'default',
        }}
      >
        <Icon
          sx={{
            color: colors.text,
            mr: 1.5,
            fontSize: 22,
          }}
        />

        <Typography
          sx={{
            flexGrow: 1,
            color: colors.text,
          }}
        >
          {message}
        </Typography>

        {isAccordion && (
          <IconButton
            aria-hidden
            tabIndex={-1}
            size="small"
            sx={{ color: colors.text }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>

      {isAccordion && renderItem && items && (
        <Collapse in={expanded}>
          <Box
            id={collapseId}
            sx={{
              px: 2,
              pb: 2,
            }}
          >
            <Stack spacing={1} sx={listSx}>
              {items.map((item, index) => renderItem(item, index))}
            </Stack>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}
